import { DateTime } from "luxon";
import { IbssPage } from "../../../../Components/Core/BasePage/IbssPage";
import
{
    Box,
    Divider,
    Grid,
    SelectChangeEvent,
    Typography,
} from "@mui/material";
import Helper from "../../../../Common/Helper";
import IbssFormControl from "../../../../Components/Forms/FormControl/IbssFormControl";
import IbssInputDropDown from "../../../../Components/Inputs/SelectList/IbssInputDropDown";
import { DateRangeHelper } from "../../../../Common/DateRangeHelper";
import { appContext } from "../../../../AppContext";
import { StringHelper } from "../../../../Common/StringHelper";
import { DateHelper } from "../../../../Common/DateHelper";
import { PagedResponse } from "../../../../Providers.Api/Models";
import LoadingOverlay from "../../../../Components/Navigation/LoadingOverlay/LoadingOverlay";
import { QueryParams } from "./QueryParams";
import { RouteComponentProps } from "react-router";
import { BookingParties, TicketedEvent } from "./DataModels";
import { GetAnnouncementDetails } from "../../../../Components/Cards/EventCard/GetAnnouncementDetails";
import IbssEventCard from "../../../../Components/Cards/EventCard/IbssEventCard";
import { rights } from "../../../../Common/RightsManager";
import IbssPageHeader from "../../../../Components/Headings/PageHeading/IbssPageHeader";
import IbssNoEventCard from "../../../../Components/Cards/EventCard/IbssNoEventCard";
import Events from "./EventsNotification.svg"

class ListEvents extends IbssPage<IProps, IState, QueryParams>
{
    private get labels() { return appContext().labels; }
    private string = new StringHelper();
    private get local() { return appContext().localStorageProvider; }
    private get apiCache() { return appContext().apiCache }
    private announcementHelper = new GetAnnouncementDetails({});
    private hasViewEvents = rights.FLEX.Events.View.has();
    private userPrefs = appContext().localStorageProvider.getUserPreferences();

    constructor(props: IProps)
    {
        super(props, new QueryParams());
        this.state =
        {
            isLoading: false,
            daysFilter:
            {
                start: DateHelper.now().startOf('week'),
                end: DateHelper.now().endOf('week')
            },
            buildingId: this.userPrefs.SearchPrefs.DefaultBuilding ?? 1,
            eventsData: [],
            buildingOptions: [],
            selectedDayOption: "",
            dayDropdownOptions: [
                {
                    label: this.labels.HubLabelThisWeek,
                    value: DayRanges.ThisWeek,
                },
                {
                    label: this.labels.HubLabelNextWeek,
                    value: DayRanges.NextWeek,
                },
                {
                    label: this.labels.HubLabelThisMonth,
                    value: DayRanges.ThisMonth,
                },
                {
                    label: this.labels.HubLabelNextMonth,
                    value: DayRanges.NextMonth,
                },
                {
                    label: this.labels.HubLabelAll,
                    value: DayRanges.All,
                },
            ],
        };
    }

    public async componentDidMount(): Promise<void>
    {
        this.pageTitle = this.labels.funcEvents_S;
    }

    private loadBuildings(): void
    {
        const buildings = Helper.getAllBuildingsData().sort((a, b) =>
            a.Name.localeCompare(b.Name)
        );

        const options = buildings.map((i) => ({ label: i.Name, value: i.Node_Id }));
        this.setState({
            buildingOptions: [
                { label: this.labels.funcAllBuildings_S, value: 1 },
                ...options,
            ],
        });
    }

    private async loadEventsByNodeId(startTime: DateTime, endTime: DateTime, nodeId: number): Promise<void> 
    {

    this.setState({ isLoading: true, eventsData: [] });
    let skipToken = ''
        const allEvents: IEvent[] = new Array<IEvent>();
    try 
    {

        do 
        {
            const filter = `Event_Start_DateTime ge datetime'${startTime.date().setZoneByNode(nodeId)}' and Event_End_DateTime lt datetime'${endTime.date().setZoneByNode(nodeId)}'`;

            const ticketedEventResponse =
                await appContext().ibssApiClientV2.v2.byNodeid.ticketedevents.get<PagedResponse<TicketedEvent[]>>({
                    nodeId: nodeId,
                    select: TicketedEvent,
                    filter: filter,
                    recursive: true,
                    top: 50,
                    skipToken: skipToken
                });

            const events = ticketedEventResponse.value;

            skipToken = ticketedEventResponse.skipToken;

            const eventBookingIds = events.map((event) => ({
                bookingId: event.Booking_Id
            }));

            const filterEventBookingsIds = eventBookingIds.map(ticketedEvent => `Booking_Id eq '${ticketedEvent.bookingId}'`).join(' or ');

            let bookingPartiesPerEventBookingIds: PagedResponse<BookingParties[]> | null = null;

            if(filterEventBookingsIds.length > 0) 
            {
                bookingPartiesPerEventBookingIds = await appContext().ibssApiClientV2.v2.byNodeid.bookingparties.get<PagedResponse<BookingParties[]>>({
                    nodeId: nodeId,
                    recursive: true,
                    select: BookingParties,
                    suppressErrorPopup: true,
                    filter: `(${filterEventBookingsIds}) and Booking_Participant_Status eq '1' and (Booking_Participant_Type eq 1 or Booking_Participant_Type eq 4 or Booking_Participant_Type eq 2)`,  //booking participant type -> 1 : attendee, 4 : owner , 2 : visitor
                    skipToken: ''
                }).catch(() => null);
            }
            const spaceList = await this.apiCache.getSpacesByBuilding(nodeId);
            const eventsData: IEvent[] = events.map((event) => 
                {
                let eventAttendeesCount = 0;
                let isUserAttending = false;
                if (bookingPartiesPerEventBookingIds?.value != null) 
                {
                    eventAttendeesCount = bookingPartiesPerEventBookingIds.value.filter((b)=> b.Booking_Id === event.Booking_Id).length
                    const currentUser = this.local.getUserDetails();
                    if (bookingPartiesPerEventBookingIds.value.some((b) => b.Booking_Participant_Email === currentUser.email && b.Booking_Id === event.Booking_Id))
                        {
                        isUserAttending = true;
                    }
                }

                const space = spaceList.find(x => x.Space_Id === event.Space_Id);
                const Space_Name = space ? space.Space_Name : '';
                return {
                    Ticketed_Event_Id: event.Ticketed_Event_Id,
                    Node_Id: event.Node_Id,
                    Booking_Id: event.Booking_Id,
                    Event_Title: event.Event_Title,
                    Event_Short_Description: event.Event_Short_Description,
                    Event_Description: event.Event_Description,
                    WebSite_URL: event.WebSite_URL,
                    ImageURI: event.ImageURI,
                    Event_Date: event.Event_Date,
                    Event_Start_DateTime: event.Event_Start_DateTime,
                    Event_End_DateTime: event.Event_End_DateTime,
                    Event_Capacity: event.Event_Capacity,
                    IsWaitingList_Allowed: event.IsWaitingList_Allowed,
                    Space_Name: Space_Name,
                    Invite_Permission: event.Invite_Permission,
                    Event_Status: event.Event_Status,
                    EventAttendees: eventAttendeesCount,
                    userIsAttending: isUserAttending,
                };
            });

            allEvents.push(...eventsData);

        } while (skipToken !== '');

    } 
    catch (error) 
    {
    }
    finally 
    {
        this.setState({
            isLoading: false,
            eventsData: allEvents,
        });
    }
}

    private eventsGroupedByYearMonth(events: IEvent[]): Map<string, IEvent[]>
    {
        return events
            .sort((a, b) =>
            {
                const yearMonth_a = a.Event_Date.toMillis();
                const yearMonth_b = b.Event_Date.toMillis();

                return yearMonth_b > yearMonth_a ? 1 : yearMonth_b < yearMonth_a ? -1 : 0;
            })
            .reduce((groupedEventsMap: Map<string, IEvent[]>, event) =>
            {
                if (!event.Event_Date)
                {
                    return groupedEventsMap;
                }
                const yearMonth = event?.Event_Date?.toFormat("yyyy-MMMM");

                if (!groupedEventsMap.has(yearMonth))
                {
                    groupedEventsMap.set(yearMonth, []);
                }

                groupedEventsMap.get(yearMonth)?.push(event);

                return groupedEventsMap;
            }, new Map<string, IEvent[]>());
    }

    private async handleBuildingChange(event: SelectChangeEvent<number>): Promise<void>
    {
        const buildingId = Number(event.target.value);
        this.pushQueryParams({ building: buildingId });
    }

    public async handleDateRangeChange(
        event: SelectChangeEvent<string>
    ): Promise<void>
    {
        const selectedDateRange = event.target.value;

        const dateRangeHelper = new DateRangeHelper(this.state.buildingId);
        let dateRange: { start: DateTime; end: DateTime } | null = null;

        switch (selectedDateRange)
        {
            case DayRanges.Today:
                dateRange = {
                    start: dateRangeHelper.addToday().getDateRange(selectedDateRange).start,
                    end: dateRangeHelper.addToday().getDateRange(selectedDateRange).start.plus({ hours: 24 })
                };
                break;

            case DayRanges.Tomorrow:
                dateRange = {
                    start: dateRangeHelper.addTomorrow().getDateRange(selectedDateRange).start,
                    end: dateRangeHelper.addTomorrow().getDateRange(selectedDateRange).start.plus({ hours: 24 })
                }
                break;

            case DayRanges.ThisWeek:
                dateRange = dateRangeHelper
                    .addThisWeek()
                    .getDateRange(selectedDateRange);
                break;

            case DayRanges.NextWeek:
                dateRange = dateRangeHelper
                    .addNextWeek()
                    .getDateRange(selectedDateRange);
                break;

            case DayRanges.ThisMonth:
                dateRange = dateRangeHelper
                    .addThisMonth()
                    .getDateRange(selectedDateRange);
                break;

            case DayRanges.NextMonth:
                dateRange = dateRangeHelper
                    .addNextMonth()
                    .getDateRange(selectedDateRange);
                break;

            case DayRanges.All:
                dateRange = dateRangeHelper.addAll().getDateRange(selectedDateRange);
                break;

            default:
                break;
        }

        if (dateRange)
        {
            const period = this.string.toKebabCase(selectedDateRange);
            this.pushQueryParams({
                period: period,
                building: this.state.buildingId,
                start: dateRange?.start,
                end: dateRange?.end,
            });
        }

    }

    public async queryParamsDidUpdate(firstLoad?: boolean, prevParams?: QueryParams): Promise<void>
    {
        const queryParams = this.queryParams;

        if (!queryParams.building || !queryParams.start || !queryParams.end || !queryParams.period)
        {
            // Push defaults into queryparams when page loads 
           return this.pushQueryParams({
                building: this.state.buildingId,
                start: this.state.daysFilter.start,
                end: this.state.daysFilter.end,
                period: this.labels.HubLabelThisWeek
            }, true)
        }

        const buildingId = queryParams.building ?? this.state.buildingId;
        const start = queryParams.start ?? this.state.daysFilter.start;
        const end = queryParams.end ?? this.state.daysFilter.end;
        const period = queryParams.period ?? this.state.selectedDayOption;

        const range = this.string.toTitleCase(period);

        await this.setStateAsync({
            buildingId: buildingId,
            daysFilter: { start: start, end: end },
            selectedDayOption: range,
        });

        this.loadBuildings();
        await this.loadEventsByNodeId(start, end, buildingId);
    }

    private navigateToDetails(nodeId: number, eventId: string): void
    {
        this.props.history.push(`/flex/events/${eventId}/${nodeId}`);
    }

    public render(): JSX.Element
    {
        const groupedEventsbyYearMonth = this.eventsGroupedByYearMonth(
            this.state.eventsData
        );
        const noEventsFound = this.state.eventsData.length === 0;
         
        return (
            <Box className="page-height-exct-header">
                <Box className="rightPanel-main-content">
                    <Grid container className="space-box-cont">
                        <Grid sm={8} mb={1}>
                        <IbssPageHeader
                            title={this.labels.funcFindAnEvents_S}
                        />
                        </Grid>
                        <Grid
                            sm={4}
                            spacing={2}
                            mb={1}
                            display={"flex"}
                            justifyContent={"end"}
                            gap={2}
                        >
                            <IbssFormControl
                                fullWidth={true}
                                sx={{
                                    backgroundColor: (theme) => theme.palette.background.paper,
                                }}
                            >
                                <IbssInputDropDown
                                    id="Time1"
                                    inputLabel=""
                                    fullWidth={true}
                                    options={this.state.dayDropdownOptions}
                                    value={
                                        this.state.selectedDayOption || this.labels.HubLabelThisWeek
                                    }
                                    onChange={(e: SelectChangeEvent<string>) =>
                                        this.handleDateRangeChange(e)
                                    }
                                />
                            </IbssFormControl>
                            <IbssFormControl
                                fullWidth={true}
                                sx={{
                                    backgroundColor: (theme) => theme.palette.background.paper,
                                }}
                            >
                                <IbssInputDropDown
                                    id="buildingSelection1"
                                    inputLabel={this.labels.HubLabelBuilding}
                                    fullWidth={true}
                                    options={this.state.buildingOptions}
                                    value={this.state.buildingId}
                                    onChange={(e: SelectChangeEvent<number>) =>
                                        this.handleBuildingChange(e)
                                    }
                                />
                            </IbssFormControl>
                        </Grid>
                    </Grid>
                    <Box style={{ marginTop: "16px" }}>
                        {this.state.isLoading && <LoadingOverlay />}
                        {noEventsFound ? (
                            <IbssNoEventCard
                                title={this.labels.funcNoEventsFound_S}
                                image={Events}
                            />
                        ) : (
                            Array.from(groupedEventsbyYearMonth.entries()).map(
                                ([yearMonth, events], index) => (
                                    <div key={index}>
                                        <Grid container my={2}>
                                            <Grid item sm={1}>
                                                <Typography variant="body2" gutterBottom>
                                                    {yearMonth}
                                                </Typography>
                                            </Grid>
                                            <Grid item sm={11}>
                                                <Divider sx={{ pt: 1 }} />
                                            </Grid>
                                        </Grid>
                                        <Grid container spacing={3}>
                                            {events.map((event) => (
                                                <Grid item sm={12} key={event.Ticketed_Event_Id}>
                                                    <IbssEventCard
                                                        title={event.Event_Title}
                                                        description={event.Event_Short_Description}
                                                        actionURL={event.WebSite_URL}
                                                        imageURI={event.ImageURI}
                                                        fullDescription={event.Event_Description}
                                                        itemDate={event.Event_Date}
                                                        startTime={event.Event_Start_DateTime}
                                                        endTime={event.Event_End_DateTime}
                                                        nodeId={event.Node_Id}
                                                        spaceName={event.Space_Name}
                                                        id={event.Ticketed_Event_Id}
                                                        status={this.announcementHelper.getEventStatus(
                                                            event.Event_Date, 
                                                            event.IsWaitingList_Allowed,
                                                            event.Event_Capacity,
                                                            event.EventAttendees,
                                                            event.userIsAttending
                                                        )}
                                                        showDetails={this.hasViewEvents}
                                                        navigateToDetails={() => this.navigateToDetails(event.Node_Id, event.Ticketed_Event_Id)}
                                                    />
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </div>
                                )
                            )
                        )}
                    </Box>
                </Box>
            </Box>
        );
    }
}

export default ListEvents;

interface IState
{
    isLoading: boolean;
    daysFilter: IDaysFilter;
    buildingId: number;
    eventsData: IEvent[];
    buildingOptions: Array<IListOption<number>>;
    selectedDayOption: string;
    dayDropdownOptions: Array<IListOption<string>>;
}
interface IProps extends RouteComponentProps { }

export interface IDaysFilter
{
    start: DateTime;
    end: DateTime;
}
export interface IListOption<TValue>
{
    label: string;
    value: TValue;
}

export interface IEvent
{
    Node_Id: number;
    Ticketed_Event_Id: string
    Booking_Id: string;
    Event_Title: string;
    Event_Short_Description: string;
    Event_Description: string;
    WebSite_URL: string;
    ImageURI: string;
    Event_Date: DateTime;
    Event_Start_DateTime: DateTime;
    Event_End_DateTime: DateTime;
    Event_Capacity: number;
    IsWaitingList_Allowed: boolean;
    Invite_Permission: string;
    Event_Status: string;
    EventAttendees: number;
    userIsAttending: boolean;
    Space_Name: string;
}

enum DayRanges
{
    Today = "Today",
    Tomorrow = "Tomorrow",
    ThisWeek = "This Week",
    NextWeek = "Next Week",
    ThisMonth = "This Month",
    NextMonth = "Next Month",
    All = "All",
}
